import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Button, Card, EmptyState, ErrorNote, Field, Input, Modal, PageHeader, Select, Spinner, statusTone, Table, Textarea } from "../components/ui";
import { PROVIDER_LABELS, STATUS_LABELS, fmtDateTime } from "../lib/format";
import { cls } from "../lib/format";

type SeedAccount = {
  id: string; email: string; provider: string; accountType: string; region: string | null; country: string | null;
  imapHost: string | null; imapPort: number | null; imapUsername: string | null; imapPasswordEnc: string | null;
  smtpHost: string | null; smtpPort: number | null; smtpUsername: string | null; status: string; connectionStatus: string;
  lastCheckedAt: string | null; lastMessageReceivedAt: string | null; groupId: string | null; notes: string | null;
  oauthEnabled: boolean; oauthClientId: string | null;
};

const EMPTY_FORM = {
  email: "", provider: "", accountType: "personal", region: "", country: "", imapHost: "", imapPort: 993,
  imapUsername: "", imapPassword: "", imapEncryption: "ssl_tls", smtpHost: "", smtpPort: 587, smtpUsername: "",
  smtpPassword: "", smtpEncryption: "starttls", status: "active", notes: "",
  oauthEnabled: false, oauthClientId: "",
};

export function SeedAccounts() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [modal, setModal] = useState<"create" | "edit" | "import" | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["seed-accounts", search, status],
    queryFn: () => api<{ items: SeedAccount[] }>(`/seed-accounts?search=${encodeURIComponent(search)}&status=${status}`),
  });
  const { data: oauthClients } = useQuery({ queryKey: ["oauth-clients"], queryFn: () => api<any[]>("/oauth/clients") });

  const save = useMutation({
    mutationFn: (payload: any) =>
      editId ? api(`/seed-accounts/${editId}`, { method: "PATCH", body: JSON.stringify(payload) }) : api("/seed-accounts", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["seed-accounts"] });
      setModal(null);
      setForm(EMPTY_FORM);
      setEditId(null);
    },
    onError: setError,
  });

  const testConnection = useMutation({
    mutationFn: (id: string) => api(`/seed-accounts/${id}/test-connection`, { method: "POST", body: JSON.stringify({ imap: true, smtp: false }) }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ["seed-accounts"] });
      alert(res.imap?.ok ? `IMAP OK: ${res.imap.message}` : `IMAP failed: ${res.imap?.message}`);
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => api(`/seed-accounts/${id}`, { method: "DELETE", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seed-accounts"] }),
  });

  const authorize = useMutation({
    mutationFn: async (accountId: string) => {
      const res = await api<{ url: string }>("/oauth/authorize", { method: "POST", body: JSON.stringify({ accountType: "seed", accountId }) });
      window.location.href = res.url;
    },
    onError: setError,
  });

  async function importCsv(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    const res = await api("/seed-accounts/import", { method: "POST", body: formData });
    setResult(res);
    qc.invalidateQueries({ queryKey: ["seed-accounts"] });
  }

  return (
    <div>
      {(params.get("oauth") === "success" || params.get("oauth") === "error") && (
        <div className={params.get("oauth") === "success" ? "mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" : "mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"}>
          {params.get("oauth") === "success" ? "Microsoft account authorized — this seed mailbox is now connected via OAuth." : `OAuth failed: ${params.get("message") ?? "unknown error"}`}
          <button className="ml-3 font-medium underline" onClick={() => setParams({})}>Dismiss</button>
        </div>
      )}
      <PageHeader
        title="Seed Accounts"
        subtitle="Recipient mailboxes used to receive placement test messages"
        actions={
          <>
            <Button variant="secondary" onClick={() => { setModal("import"); setError(null); setResult(null); }}>CSV Import</Button>
            <Button onClick={() => { setForm(EMPTY_FORM); setEditId(null); setError(null); setModal("create"); }}>+ Add Seed Account</Button>
          </>
        }
      />
      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-3 border-b border-ink-100 p-4">
          <Input placeholder="Search email…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="max-w-[160px]">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
          <a href="/api/v1/seed-accounts/import-template" className="text-xs font-medium text-brand-600 hover:text-brand-700">Download CSV template</a>
        </div>
        {isLoading ? (
          <Spinner />
        ) : !data?.items.length ? (
          <div className="p-6"><EmptyState title="No seed accounts" description="Add seed mailboxes manually or import them from CSV." /></div>
        ) : (
          <Table headers={["Email", "Provider", "Type", "Region", "IMAP", "Last Checked", "Last Received", "Status", "Actions"]}>
            {data!.items.map((a) => (
              <tr key={a.id} className="table-row-hover">
                <td className="px-4 py-3 font-medium text-ink-800">{a.email}</td>
                <td className="px-4 py-3"><Badge tone="neutral">{PROVIDER_LABELS[a.provider] ?? a.provider}</Badge></td>
                <td className="px-4 py-3 capitalize text-ink-600">{a.accountType}</td>
                <td className="px-4 py-3 text-ink-500">{a.region ?? "—"}</td>
                <td className="px-4 py-3"><Badge tone={statusTone(a.connectionStatus)}>{a.connectionStatus}</Badge></td>
                <td className="px-4 py-3 text-xs text-ink-500">{fmtDateTime(a.lastCheckedAt)}</td>
                <td className="px-4 py-3 text-xs text-ink-500">{fmtDateTime(a.lastMessageReceivedAt)}</td>
                <td className="px-4 py-3"><Badge tone={a.status === "active" ? "success" : "neutral"}>{STATUS_LABELS[a.status]}</Badge></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button className="rounded px-2 py-1 text-xs text-brand-600 hover:bg-brand-50" onClick={() => { setForm({ ...EMPTY_FORM, ...a, region: a.region ?? "", country: a.country ?? "", imapHost: a.imapHost ?? "", imapUsername: a.imapUsername ?? "", imapPort: a.imapPort ?? 993, smtpHost: a.smtpHost ?? "", smtpUsername: a.smtpUsername ?? "", smtpPort: a.smtpPort ?? 587, oauthClientId: a.oauthClientId ?? "", notes: a.notes ?? "", imapPassword: "", smtpPassword: "" }); setEditId(a.id); setModal("edit"); }}>Edit</button>
                    <button className="rounded px-2 py-1 text-xs text-ink-500 hover:bg-ink-50" onClick={() => testConnection.mutate(a.id)}>Test</button>
                    <button className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50" onClick={() => { if (confirm(`Delete ${a.email}?`)) del.mutate(a.id); }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal === "create" || modal === "edit"} onClose={() => setModal(null)} title={editId ? "Edit Seed Account" : "Add Seed Account"} wide>
        <div className="space-y-4">
          <ErrorNote error={error} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Email Address"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="seed@gmail.com" /></Field>
            <Field label="Provider">
              <Select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
                <option value="">Auto-detect</option>
                {Object.entries(PROVIDER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </Field>
            <Field label="Account Type">
              <Select value={form.accountType} onChange={(e) => setForm({ ...form, accountType: e.target.value })}>
                <option value="professional">Professional / Business</option>
                <option value="personal">Personal / Consumer</option>
              </Select>
            </Field>
            <Field label="Region"><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="North America" /></Field>
            <Field label="Country"><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="US" /></Field>
          </div>
          <div className="rounded-lg border border-ink-200 p-4">
            <h4 className="mb-3 text-xs font-semibold uppercase text-ink-500">IMAP</h4>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="IMAP Host"><Input value={form.imapHost} onChange={(e) => setForm({ ...form, imapHost: e.target.value })} placeholder="imap.gmail.com" /></Field>
              <Field label="IMAP Port"><Input type="number" value={form.imapPort} onChange={(e) => setForm({ ...form, imapPort: Number(e.target.value) })} /></Field>
              <Field label="IMAP Encryption">
                <Select value={form.imapEncryption} onChange={(e) => setForm({ ...form, imapEncryption: e.target.value })}>
                  <option value="ssl_tls">SSL/TLS</option>
                  <option value="starttls">STARTTLS</option>
                  <option value="none">None</option>
                </Select>
              </Field>
              <Field label="IMAP Username"><Input value={form.imapUsername} onChange={(e) => setForm({ ...form, imapUsername: e.target.value })} /></Field>
              <Field label="IMAP Password / App Password"><Input type="password" value={form.imapPassword} onChange={(e) => setForm({ ...form, imapPassword: e.target.value })} placeholder={editId ? "•••••••••••• (leave blank to keep)" : ""} /></Field>
            </div>
          </div>
          <div className="rounded-lg border border-ink-200 p-4">
            <h4 className="mb-3 text-xs font-semibold uppercase text-ink-500">SMTP</h4>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="SMTP Host"><Input value={form.smtpHost} onChange={(e) => setForm({ ...form, smtpHost: e.target.value })} /></Field>
              <Field label="SMTP Port"><Input type="number" value={form.smtpPort} onChange={(e) => setForm({ ...form, smtpPort: Number(e.target.value) })} /></Field>
              <Field label="SMTP Encryption">
                <Select value={form.smtpEncryption} onChange={(e) => setForm({ ...form, smtpEncryption: e.target.value })}>
                  <option value="starttls">STARTTLS</option>
                  <option value="ssl_tls">SSL/TLS</option>
                  <option value="none">None</option>
                </Select>
              </Field>
              <Field label="SMTP Username"><Input value={form.smtpUsername} onChange={(e) => setForm({ ...form, smtpUsername: e.target.value })} /></Field>
              <Field label="SMTP Password"><Input type="password" value={form.smtpPassword} onChange={(e) => setForm({ ...form, smtpPassword: e.target.value })} placeholder={editId ? "•••••••••••• (leave blank to keep)" : ""} /></Field>
            </div>
          </div>
          <div className="rounded-lg border border-ink-200 p-4">
            <h4 className="mb-3 text-xs font-semibold uppercase text-ink-500">Microsoft OAuth (XOAUTH2)</h4>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.oauthEnabled} onChange={(e) => setForm({ ...form, oauthEnabled: e.target.checked })} className="h-4 w-4 rounded text-brand-600" />
                Use Microsoft OAuth instead of an app password
              </label>
              {form.oauthEnabled && (
                <>
                  <Select value={form.oauthClientId} onChange={(e) => setForm({ ...form, oauthClientId: e.target.value })} className="max-w-[220px]">
                    <option value="">Select OAuth client…</option>
                    {oauthClients?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                  {editId && form.oauthClientId ? (
                    <Button variant="secondary" size="sm" onClick={() => authorize.mutate(editId)} disabled={authorize.isPending}>
                      {authorize.isPending ? "Redirecting…" : "Authorize with Microsoft"}
                    </Button>
                  ) : (
                    <span className="text-xs text-ink-400">Save the account first, then open it again to authorize.</span>
                  )}
                </>
              )}
            </div>
          </div>
          <Field label="Status">
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </Field>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={() => save.mutate(form)} disabled={save.isPending}>{editId ? "Save changes" : "Add account"}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={modal === "import"} onClose={() => setModal(null)} title="Import Seed Accounts (CSV)">
        <div className="space-y-4">
          <p className="text-sm text-ink-500">Upload a CSV with columns: email, provider, account_type, imap_host, imap_port, imap_username, imap_password, imap_encryption, smtp_host, smtp_port, smtp_username, smtp_password, smtp_encryption, country, region, group, status.</p>
          <input ref={fileRef} type="file" accept=".csv" className="block w-full text-sm" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file) await importCsv(file);
          }} />
          {result && (
            <div className="rounded-lg border border-ink-200 bg-ink-50 p-4 text-sm">
              Processed <strong>{result.processed}</strong> rows: <span className="text-emerald-600">{result.succeeded} succeeded</span>, <span className="text-red-600">{result.failed} failed</span>
              {result.errors?.length > 0 && (
                <ul className="mt-2 max-h-40 overflow-auto space-y-1 text-xs">
                  {result.errors.map((e: any, i: number) => <li key={i}>Row {e.row} ({e.email}): {e.message}</li>)}
                </ul>
              )}
            </div>
          )}
          <div className="flex justify-end"><Button variant="secondary" onClick={() => setModal(null)}>Close</Button></div>
        </div>
      </Modal>
    </div>
  );
}
